/**
 * @fileoverview A function that provides the participants API.
 *
 * @example
 * const { result } = renderHook(() => useParticipantsAPI())
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { States } from '../config'
import { axiosBaseQuery } from './utils/axiosBaseQuery'

/**
 * @interface UpdateParticipantStatusPayload
 * @description The payload for updating the participant status.
 */
export interface UpdateParticipantStatusPayload {
    credentials?: {
        username: string
        password: string
    }
    set: {
        study_id: string
        patient_id: string
        from?: string
        status: string
        annotation?: string
    }[]
}

export const participantsApi = createApi({
    reducerPath: 'participantsApi',
    baseQuery: axiosBaseQuery({ baseUrl: `/private/patients` }),
    tagTypes: ['Participant', 'ParticipantCounter'],
    endpoints: (builder) => ({
        createParticipant: builder.mutation<Novoic.API.Participant, Record<string, unknown>>({
            query: (data) => {
                return {
                    url: '',
                    method: 'POST',
                    data,
                }
            },
        }),

        updateParticipantStatus: builder.mutation<
            UpdateParticipantStatusPayload,
            Partial<UpdateParticipantStatusPayload>
        >({
            query: (data) => {
                return {
                    url: '/status',
                    method: 'POST',
                    data,
                }
            },
            invalidatesTags: (result, error, args) => {
                const invalidatedParticipants = args.set.map(({ patient_id }) => ({
                    type: 'Participant' as const,
                    id: patient_id,
                }))

                const invalidatedCounters = [] as { type: 'ParticipantCounter'; id: string }[]

                args.set.forEach(({ from, status }) => {
                    invalidatedCounters.push({
                        type: 'ParticipantCounter' as const,
                        id: `status=${from}`,
                    })

                    invalidatedCounters.push({
                        type: 'ParticipantCounter' as const,
                        id: `status=${status}`,
                    })
                })

                return [...invalidatedParticipants, ...invalidatedCounters]
            },
        }),

        updateParticipantStudyId: builder.mutation<
            void,
            {
                participantId: string
                studyId: string
                status: States
                annotation?: string
                credentials: { username: string; password: string }
                targetStudyId: string
            }
        >({
            query: ({ participantId, studyId, status, credentials, annotation, targetStudyId }) => {
                return {
                    url: `/${participantId}/studies/${studyId}`,
                    method: 'PUT',
                    data: {
                        status,
                        to_study_id: targetStudyId,
                        annotation,
                        ...credentials,
                    },
                }
            },
            invalidatesTags: (result, error, args) => {
                // TODO: Invalidate study counters as well as study participants cache
                return [
                    {
                        type: 'Participant' as const,
                        id: args.participantId,
                    },
                ]
            },
        }),

        addExternalId: builder.mutation<
            { patient_id: string; name: string; external_id: string; id: string },
            { participantId: string; name: string; externalId: string }
        >({
            query: (data) => {
                return {
                    url: `/${data.participantId}/externals`,
                    method: 'POST',
                    data: {
                        name: data.name,
                        external_id: data.externalId,
                    },
                }
            },
            invalidatesTags: (result, error, args) => {
                return [
                    {
                        type: 'Participant' as const,
                        id: args.participantId,
                    },
                ]
            },
        }),

        removeExternalId: builder.mutation<
            void,
            {
                participantId: string
                externalId: string
            }
        >({
            query: ({ externalId, participantId }) => {
                return {
                    url: `/${participantId}/externals/${externalId}`,
                    method: 'DELETE',
                }
            },
            invalidatesTags: (result, error, args) => {
                return [
                    {
                        type: 'Participant' as const,
                        id: args.participantId,
                    },
                ]
            },
        }),

        getParticipantPhoneNumber: builder.query<{ phone: string; expire_after: number }, string>({
            query: (participantId) => {
                return {
                    url: `/${participantId}/contacts/phone`,
                    method: 'GET',
                }
            },
        }),

        getParticipants: builder.query<Novoic.API.Participant[], string>({
            query: (query?: string) => {
                return {
                    url: `?${query}`,
                    method: 'GET',
                }
            },
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({ type: 'Participant' as const, id })),
                          'Participant',
                      ]
                    : ['Participant'],
        }),

        getParticipantById: builder.query<Novoic.API.Participant, string>({
            query: (id: string) => {
                return {
                    url: `/${id}`,
                    method: 'GET',
                }
            },
            providesTags: (result) => [{ type: 'Participant' as const, id: result?.id }],
        }),

        notifyParticipantByPhone: builder.mutation<
            void,
            { participantId: string; invitationId: string }
        >({
            query: (data) => {
                return {
                    url: `/${data.participantId}/invites/${data.invitationId}/phone`,
                    method: 'POST',
                }
            },
        }),

        notifyParticipantByEmail: builder.mutation<
            void,
            { participantId: string; invitationId: string }
        >({
            query: (data) => {
                return {
                    url: `/${data.participantId}/invites/${data.invitationId}/email`,
                    method: 'POST',
                }
            },
        }),

        getParticipantsCount: builder.query<{ count: number }, string>({
            query: (query) => {
                return {
                    url: `/count?${query}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, args) => [
                { type: 'ParticipantCounter' as const, id: args },
            ],
        }),

        getParticipantsForStudyId: builder.query<Novoic.API.Participant[], string>({
            query: (studyId: string) => {
                const query = new URLSearchParams(location.search)
                query.append('study', studyId)

                return {
                    url: `?${query.toString()}`,
                    method: 'GET',
                }
            },
        }),

        getParticipantsCountForStudyId: builder.query<{ count: number }, string>({
            query: (studyId: string) => {
                const query = new URLSearchParams(location.search)
                query.append('study', studyId)
                ;['review', 'qualified', 'invited', 'not_qualified', 'excluded'].forEach(
                    (status) => {
                        query.append('status', status)
                    }
                )
                return {
                    url: `/count?${query.toString()}}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, args) => [
                { type: 'ParticipantCounter' as const, id: args },
            ],
        }),
    }),
})

export const {
    useCreateParticipantMutation,
    useUpdateParticipantStatusMutation,
    useAddExternalIdMutation,
    useRemoveExternalIdMutation,
    useGetParticipantPhoneNumberQuery,
    useGetParticipantsQuery,
    useGetParticipantsCountQuery,
    useGetParticipantByIdQuery,
    useGetParticipantsForStudyIdQuery,
    useGetParticipantsCountForStudyIdQuery,
    useNotifyParticipantByPhoneMutation,
    useNotifyParticipantByEmailMutation,
    useUpdateParticipantStudyIdMutation,
} = participantsApi
